import React, { useEffect, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, themeQuartz } from 'ag-grid-community';
import '../../config/agGridConfig'; // Register AG Grid modules
import { adminAPI } from '../../services/api';
import IntegrationFormModal from '../../components/admin/IntegrationFormModal';
import ConfirmDialog from '../../components/admin/ConfirmDialog';
import Toast from '../../components/admin/Toast';
import '../../styles/AdminGlobal.css';
import './Admin.css';

const IntegrationsManagement: React.FC = () => {
  const [integrations, setIntegrations] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<any | null>(null);
  const [toast, setToast] = useState<{show: boolean, message: string, type: 'success' | 'error'}>({
    show: false,
    message: '',
    type: 'success'
  });

  const columnDefs: ColDef[] = [
    { field: 'id', headerName: 'ID', width: 70, sortable: true },
    { 
      field: 'display_name', 
      headerName: 'Display Name', 
      sortable: true, 
      filter: true, 
      flex: 1 
    },
    { 
      field: 'integration_type_display', 
      headerName: 'Type', 
      sortable: true, 
      width: 150,
      cellRenderer: (params: any) => (
        <span className="type-badge">
          {params.value}
        </span>
      )
    },
    { 
      field: 'environment_display', 
      headerName: 'Environment', 
      sortable: true, 
      width: 130,
      cellRenderer: (params: any) => (
        <span className={`env-badge ${params.data.environment?.toLowerCase()}`}>
          {params.value}
        </span>
      )
    },
    { 
      field: 'api_key_masked', 
      headerName: 'API Key', 
      width: 150
    },
    { 
      field: 'is_active', 
      headerName: 'Status', 
      width: 120,
      cellRenderer: (params: any) => (
        <span className={`status-badge ${params.value ? 'approved' : 'pending'}`}>
          {params.value ? 'Active' : 'Inactive'}
        </span>
      )
    },
    { 
      field: 'updated_at', 
      headerName: 'Last Updated', 
      sortable: true, 
      width: 150,
      valueFormatter: (p: any) => new Date(p.value).toLocaleDateString()
    },
    { 
      headerName: 'Actions',
      width: 100,
      pinned: 'right',
      lockPosition: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      cellRenderer: (params: any) => {
        const buttonStyle = {
          border: 'none',
          borderRadius: '6px',
          width: '32px',
          height: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          fontSize: '16px',
          transition: 'all 0.2s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        };
        
        return (
          <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
            <button 
              onClick={() => handleEdit(params.data)}
              title="Edit Integration"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#007bff',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              ✏️
            </button>
            <button 
              onClick={() => handleDeleteClick(params.data)}
              title="Delete Integration"
              style={{ 
                ...buttonStyle,
                backgroundColor: '#dc3545',
                color: 'white'
              }}
              onMouseEnter={(e) => (e.currentTarget.style.transform = 'scale(1.1)')}
              onMouseLeave={(e) => (e.currentTarget.style.transform = 'scale(1)')}
            >
              🗑️
            </button>
          </div>
        );
      }
    }
  ];

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      const response = await adminAPI.getIntegrations();
      const data = response.data || response;
      const integrationsList = Array.isArray(data) ? data : (data.results || []);
      setIntegrations(integrationsList);
    } catch (error) {
      console.error('Failed to load integrations:', error);
      setToast({ show: true, message: 'Failed to load integrations', type: 'error' });
      setIntegrations([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setSelectedIntegration(null);
    setIsModalOpen(true);
  };

  const handleEdit = (integration: any) => {
    setSelectedIntegration(integration);
    setIsModalOpen(true);
  };

  const handleDeleteClick = (integration: any) => {
    setSelectedIntegration(integration);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedIntegration) return;
    
    try {
      await adminAPI.deleteIntegration(selectedIntegration.id);
      setToast({ show: true, message: 'Integration deleted successfully', type: 'success' });
      loadIntegrations();
    } catch (error) {
      console.error('Delete error:', error);
      setToast({ show: true, message: 'Failed to delete integration', type: 'error' });
    } finally {
      setIsDeleteDialogOpen(false);
      setSelectedIntegration(null);
    }
  };

  const handleSave = async (formData: any) => {
    try {
      if (selectedIntegration) {
        await adminAPI.updateIntegration(selectedIntegration.id, formData);
        setToast({ show: true, message: 'Integration updated successfully', type: 'success' });
      } else {
        await adminAPI.createIntegration(formData);
        setToast({ show: true, message: 'Integration created successfully', type: 'success' });
      }
      setIsModalOpen(false);
      setSelectedIntegration(null);
      loadIntegrations();
    } catch (error: any) {
      console.error('Save error:', error);
      throw error; // Re-throw to let form handle validation errors
    }
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  // Ensure integrations is always an array before filtering
  const integrationsArray = Array.isArray(integrations) ? integrations : [];
  const filteredIntegrations = integrationsArray.filter(integration =>
    integration.service_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    integration.display_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="admin-page">
      <div className="management-header">
        <div>
          <h3>Integrations Management</h3>
          <p>Configure third-party service integrations</p>
        </div>
        <div className="management-actions">
          <button className="btn btn-success" onClick={handleAddNew}>
            + Add Integration
          </button>
          <button className="btn btn-secondary" onClick={loadIntegrations}>
            🔄 Refresh
          </button>
        </div>
      </div>
      
      <div className="ag-grid-wrapper">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search integrations by name..."
            value={searchTerm}
            onChange={handleSearch}
          />
        </div>
        <div className="results-info">
          <span>{filteredIntegrations.length}</span> integrations found
        </div>
      
        <div className="ag-grid-container">
          <AgGridReact

            theme={themeQuartz}
            rowData={filteredIntegrations || []}
            columnDefs={columnDefs}
            pagination={true}
            paginationPageSize={20}
            loading={loading}

            defaultColDef={{
              resizable: true,
            }}
          />
        </div>
      </div>

      <IntegrationFormModal
        isOpen={isModalOpen}
        onClose={() => { 
          setIsModalOpen(false); 
          setSelectedIntegration(null); 
        }}
        onSave={handleSave}
        integration={selectedIntegration}
      />

      <ConfirmDialog
        isOpen={isDeleteDialogOpen}
        onClose={() => {
          setIsDeleteDialogOpen(false);
          setSelectedIntegration(null);
        }}
        onConfirm={handleDelete}
        title="Delete Integration"
        message={`Are you sure you want to delete "${selectedIntegration?.display_name || selectedIntegration?.service_name}"? This action cannot be undone.`}
        type="danger"
        confirmText="Delete"
      />

      {toast.show && (
        <Toast
          {...toast}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default IntegrationsManagement;
